import React from 'react';
import {
  constants,
  countMultipleBonusOdds,
  countMultipleTotalBonusOdds,
  getConfigProp,
  isEmptyCollection,
  NumberService,
} from '@atlas/core';

import {
  BetOdds,
  Button,
  FreebetBonusSelect,
  Icon,
  Label,
  Loader,
  MoneyInput,
  MoneyValue,
} from '@atlas/ui';

import cx from 'classnames';

export function FinalOddsSection({ selectedBet, multipleBonus }) {
  if (selectedBet.betType === constants.BET_TYPE.SYSTEM) {
    return null;
  }

  if (!multipleBonus || !multipleBonus.bonusOnMultipleBetCoefficient) {
    return (
      <div className="betslip-controls-info__item">
        <Label message="final_odds" className="betslip-controls-info__name" />
        <BetOdds className="betslip-controls-info__amount" odds={selectedBet.odds} />
      </div>
    );
  }

  const bonusOdds = countMultipleBonusOdds(selectedBet.odds, multipleBonus);

  return (
    <div className="betslip-controls-info__item">
      <Label message="final_odds" className="betslip-controls-info__name" />

      <BetOdds className="betslip-controls-info__old-amount" odds={selectedBet.odds} />

      <BetOdds className="betslip-controls-info__amount" odds={bonusOdds} />

      <Icon name="bonuses" />
    </div>
  );
}

export function FinalOddsSectionWithPercentage({
  selectedBet,
  multipleBonus,
  betAmount,
  currency,
}) {
  if (selectedBet.betType === constants.BET_TYPE.SYSTEM) {
    return null;
  }

  if (!multipleBonus || !multipleBonus.bonusOnMultipleBetCoefficient) {
    return (
      <div className="betslip-controls-info__item">
        <Label message="final_odds" className="betslip-controls-info__name" />
        <BetOdds className="betslip-controls-info__amount" odds={selectedBet.odds} />
      </div>
    );
  }

  return (
    <div className="betslip-controls-info__item-percentage">
      <div className="item-content">
        <Label message="total_odds" className="betslip-controls-info__text" />
        :
        <BetOdds className="betslip-controls-info__amount-percentage" odds={selectedBet.odds} />
      </div>

      <div className="item-content">
        <Label message="possible_win" className="betslip-controls-info__text" />
        :
        <MoneyValue
          className="betslip-controls-info__amount-percentage"
          currency={currency}
          value={selectedBet.odds * betAmount}
        />
      </div>

      <div className="item-content">
        +
        <Label message="express_bonus" className="betslip-controls-info__name" />:
        <div className="betslip-controls-info__amount-percentage">
          {NumberService.oddsToPercentage(multipleBonus.bonusOnMultipleBetCoefficient)}
        </div>
      </div>
    </div>
  );
}

export function PossibleWinSection(props) {
  const { multipleBonus, freebetBonus, betAmount, selectedBet, currency } = props;

  const possibleWin = React.useMemo(() => {
    if (freebetBonus) {
      return selectedBet.odds * freebetBonus.freeBetAmount - freebetBonus.freeBetAmount;
    }

    if (!betAmount) {
      return null;
    }

    if (multipleBonus) {
      return countMultipleTotalBonusOdds(selectedBet.odds, betAmount, multipleBonus);
    }

    return selectedBet.odds * betAmount;
  }, [multipleBonus, freebetBonus, betAmount, selectedBet.odds]);

  if (!possibleWin) {
    return null;
  }

  return (
    <div className="betslip-controls-info__item">
      <Label message="total_possible_win" className="betslip-controls-info__name" />
      :
      <MoneyValue
        className="betslip-controls-info__amount"
        currency={currency}
        value={possibleWin}
      />
    </div>
  );
}

export default function MoneyBlock(props) {
  const {
    selectedBet,
    selectedFreebet,
    betAmount,
    setBetAmount,
    currency,
    disabled,
    loading,
    authenticated,
    bonuses,
    selectBonus,
    removeSelectedBonuses,
    onMaxBetClick,
    maxBetParams,
  } = props;
  const isSportRadarProject = getConfigProp('isSportRadarProject');

  const inputDisabled = disabled || loading;

  const multipleBonus = React.useMemo(() => {
    if (selectedFreebet || isEmptyCollection(bonuses)) {
      return null;
    }

    return bonuses.find(bonus => bonus.bonusType === constants.BONUS_TYPE.MULTIPLE_BET);
  }, [selectedFreebet, bonuses]);

  const shouldDisplayMaxBetButton = authenticated && selectedBet.active;

  const inputWithBtnClassName = cx('input-with-btn', { disabled: inputDisabled });

  return (
    <div className="betslip-controls-money">
      <div className="betslip-controls__inner">
        <div className={inputWithBtnClassName}>
          <MoneyInput
            key={selectedBet.outcomeId || selectedBet.requestId}
            className="betslip-controls-money__input"
            label="amount"
            value={selectedFreebet?.freeBetAmount || betAmount}
            currency={currency}
            disabled={inputDisabled}
            onChange={setBetAmount}
          />
          {shouldDisplayMaxBetButton && (
            <Button onClick={onMaxBetClick} type="outline" size="large" disabled={disabled}>
              {loading ? <Loader /> : <Label message="max_bet" params={maxBetParams} />}
            </Button>
          )}
        </div>
      </div>

      {!isSportRadarProject && (
        <FreebetBonusSelect
          selectedFreebet={selectedFreebet}
          bonuses={bonuses}
          selectBonus={selectBonus}
          removeSelectedBonuses={removeSelectedBonuses}
          disabled={loading}
        />
      )}

      {selectedBet.odds && (
        <div className="betslip-controls-info">
          <FinalOddsSectionWithPercentage
            selectedBet={selectedBet}
            multipleBonus={multipleBonus}
            betAmount={betAmount}
            currency={currency}
          />

          <PossibleWinSection
            betAmount={betAmount}
            multipleBonus={multipleBonus}
            freebetBonus={selectedFreebet}
            selectedBet={selectedBet}
            currency={currency}
          />
        </div>
      )}
    </div>
  );
}
